<template>
  <cs-preloader-overlay :loading="loading">
    <div :class="$style.container">
      <div :class="$style.header">
        <responsive-controller>
          <template #tablet>
            <screen-header :title="displayTitle" />
          </template>
          <template #default>
            <div>
              <navigate-button
                v-absolute
                v-left="-80"
                @navigate="$router.go(-1)"
              />
              <screen-title :title="displayTitle" />
            </div>
          </template>
        </responsive-controller>
      </div>

      <report-builder-form
        v-model="formData"
        :metrics="metrics"
        :partners="getChildProjects"
      >
        <masked-input
          slot="period"
          v-model="unloadingPeriod"
          :mask="periodPatternMask"
          :label="$t('unloading.period')"
          :placeholder="$t('enter.period')"
          @change="parseUploadingPeriod"
        >
          <div
            slot="link"
            :class="$style.link"
            @click="handleShownDateRange"
          >
            {{ $t('common.choose') }}
          </div>
        </masked-input>
      </report-builder-form>

      <div :class="$style.building">
        <link-button
          :href="`/report/download?${getSerializedQuery}`"
          :disabled="disableSubmit"
          target="_blank"
        >
          {{ $t('get.report') }}
        </link-button>
      </div>

      <div :class="$style.saving">
        <cs-button
          width="250px"
          type="secondary"
          :disabled="disableSubmit"
          @click="handleManagingTemplate"
        >
          {{ $t('save.template') }}
        </cs-button>
      </div>

      <selecting-transaction-types />
      <selecting-transaction-statuses />

      <selecting-date-range />

      <selecting-period-preset />

      <selecting-report-metrics
        :metrics="metrics"
        :report-type="reportType"
      />

      <selecting-partners
        :partners="getChildProjects"
      />
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapGetters } from 'vuex';
import { reverseString } from 'lib/utils/string';
import serializeParams from 'lib/utils/url';

import {
  getMomentConstructor,
  subtractDaysFromDate,
  getDateStartTimeAsISOString,
  getDateEndTimeAsISOString,
  formatDateToCustomDateFormat
} from 'lib/utils/date';

import {
  createTemplate as createTemplateApi,
  editTemplate as editTemplateApi,
  fetchTemplateById as fetchTemplateByIdApi,
  fetchReportFeilds as fetchReportFeildsApi
} from 'services/api/report';

import { EVENT_BUS } from '@/eventBus';

import MaskedInput from 'molecules/capusta-input/MaskedInput';
import LinkButton from 'molecules/common/LinkButton';

import ScreenTitle from 'molecules/screen/ScreenTitle';
import ScreenHeader from 'molecules/screen/ScreenHeader';
import NavigateButton from 'molecules/common/NavigateButton';

import ReportBuilderForm from 'organisms/reports/ReportBuilderForm';

import SelectingTransactionTypes from 'organisms/reports/SelectingTransactionTypes';
import SelectingTransactionStatuses from 'organisms/reports/SelectingTransactionStatuses';
import SelectingPeriodPreset from 'organisms/reports/SelectingPeriodPreset';
import SelectingDateRange from 'organisms/reports/SelectingDateRange';
import SelectingReportMetrics from 'organisms/reports/SelectingReportMetrics';
import SelectingPartners from 'organisms/reports/SelectingPartners';

import {
  TRANSACTION_TYPE_LIST,
  TRANSACTION_STATUS_LIST
} from 'organisms/reports/const';

const moment = getMomentConstructor();

export default {
  name: 'ReportBuilder',
  components: {
    MaskedInput,
    LinkButton,
    ScreenTitle,
    ScreenHeader,
    NavigateButton,
    ReportBuilderForm,
    SelectingTransactionTypes,
    SelectingTransactionStatuses,
    SelectingPeriodPreset,
    SelectingDateRange,
    SelectingReportMetrics,
    SelectingPartners,
  },
  async created() {
    EVENT_BUS.$on('SELECTED_TRANSACTION_TYPES', types => {
      this.formData.transactionTypes = types;
    });

    EVENT_BUS.$on('SELECTED_TRANSACTION_STATUSES', statuses => {
      this.formData.transactionStatuses = statuses;
    });

    EVENT_BUS.$on('SELECTED_PARTNERS', partners => {
      this.formData.partners = partners;
    });

    EVENT_BUS.$on('LOAD_PARTNERS', partners => {
      if (!this.formData.partners.length) {
        this.formData.partners = partners;
      }
    });

    EVENT_BUS.$on('SELECTED_METRICS', metrics => {
      this.formData.metrics = metrics;
    });

    EVENT_BUS.$on('SELECTED_DATE_RANGE', this.setUploadingPeriod);

    EVENT_BUS.$on('SELECTED_PERIOD_PRESET', this.setUploadingPeriod);

    EVENT_BUS.$on('EVENT_SAVE_TEMPLATE', (name) => {
      this.templateName = name;
      this.saveReportTemplate();
    });

    this.setUploadingPeriod();

    await Promise.all([
      this.loadMetrics(),
      this.loadReport(),
    ]);
  },
  destroyed() {
    EVENT_BUS.$off('EVENT_SAVE_TEMPLATE');
    EVENT_BUS.$off('SELECTED_TRANSACTION_TYPES');
    EVENT_BUS.$off('SELECTED_TRANSACTION_STATUSES');
    EVENT_BUS.$off('SELECTED_PARTNERS');
    EVENT_BUS.$off('LOAD_PARTNERS');
    EVENT_BUS.$off('SELECTED_METRICS');
    EVENT_BUS.$off('SELECTED_DATE_RANGE');
    EVENT_BUS.$off('SELECTED_PERIOD_PRESET');
  },
  data() {
    return {
      loading: false,
      templateName: '',
      metrics: [],
      formData: {
        id: null,
        dateFrom: moment(subtractDaysFromDate(new Date, 29)),
        dateTo: moment(new Date()),
        metrics: [],
        partners: [],
        transactionTypes: '',
        transactionStatuses: '',
      },
      unloadingPeriod: '',
      periodPatternMask: [/\d/, /\d/, '.', /\d/, /\d/, '.', /\d/, /\d/, /\d/, /\d/, ' ', '-', ' ', /\d/, /\d/, '.', /\d/, /\d/, '.', /\d/, /\d/, /\d/, /\d/],
    };
  },
  computed: {
    reportType() {
      return this.$route.query.type;
    },
    displayTitle() {
      return this.$route.query.type === 'PARTNER'
        ? this.$t('partner.report')
        : this.$t('transaction.report');
    },
    merchantTimezoneCode() {
      return this.getMerchantTimezone.code;
    },
    selectedMetrics() {
      if (this.$validate.isEmptyArray(this.formData.metrics)) {
        return this.metrics;
      }

      return this.formData.metrics;
    },
    selectedProjects() {
      if (this.$validate.isEmptyArray(this.formData.partners)) {
        return this.getChildProjects;
      }

      return this.formData.partners;
    },
    formattedMetricsNames() {
      return this.selectedMetrics.map(metric => metric.name).join(',');
    },
    formattedProjectIds() {
      return this.selectedProjects?.map(partner => partner.projectId).join(',') ?? '';
    },
    formattedTransactionTypes() {
      if (this.$validate.isEmptyString(this.formData.transactionTypes)) {
        return TRANSACTION_TYPE_LIST.join(',');
      }

      return this.formData.transactionTypes;
    },
    formattedTransactionStatuses() {
      if (this.$validate.isEmptyString(this.formData.transactionStatuses)) {
        return TRANSACTION_STATUS_LIST.join(',');
      }

      return this.formData.transactionStatuses;
    },
    templatePayload() {
      return {
        type: this.reportType,
        name: this.templateName,
        dateFrom: getDateStartTimeAsISOString(this.formData.dateFrom),
        dateTo: getDateEndTimeAsISOString(this.formData.dateTo),
        fields: this.selectedMetrics,
        projects: this.selectedProjects,
        transactionTypes: this.formattedTransactionTypes,
        transactionStatuses: this.formattedTransactionStatuses,
      };
    },
    getSerializedQuery() {
      return serializeParams({
        type: this.reportType,
        dateFrom: getDateStartTimeAsISOString(this.formData.dateFrom),
        dateTo: getDateEndTimeAsISOString(this.formData.dateTo),
        projects: this.formattedProjectIds,
        fields: this.formattedMetricsNames,
        transactionTypes: this.formattedTransactionTypes,
        transactionStatuses: this.formattedTransactionStatuses,
      });
    },
    disableSubmit() {
      const { dateFrom, dateTo } = this.formData;

      if (!dateFrom || !dateTo) {
        return true;
      }

      if (dateFrom.isValid() && dateTo.isValid()) {
        return false;
      }

      return true;
    },
    ...mapGetters(['getMerchantTimezone']),
    ...mapGetters('partnership', ['getChildProjects']),
  },
  methods: {
    parseUploadingPeriod() {
      const val = this.unloadingPeriod;
      const moment = getMomentConstructor();

      const [dateFrom = '', dateTo = ''] = (val || '').split(' - ');

      const preparedDateFrom = reverseString(dateFrom, '.').replace(/\./g, '/');
      const preparedDateTo = reverseString(dateTo, '.').replace(/\./g, '/');

      this.formData.dateFrom = moment(preparedDateFrom)
        .startOf('day')
        .tz(this.merchantTimezoneCode, true);

      this.formData.dateTo = moment(preparedDateTo)
        .endOf('day')
        .tz(this.merchantTimezoneCode, true);
    },
    setUploadingPeriod(preset) {
      const { dateFrom, dateTo } = preset || this.formData;

      const formattedDateFrom = dateFrom.isValid()
        ? formatDateToCustomDateFormat(dateFrom, 'DD.MM.YYYY') : '';

      const formattedDateTo = dateTo.isValid()
        ? formatDateToCustomDateFormat(dateTo, 'DD.MM.YYYY') : '';

      if (!formattedDateFrom && !formattedDateTo) {
        return '';
      }

      this.unloadingPeriod = `${formattedDateFrom} - ${formattedDateTo}`;

      this.formData.dateFrom = dateFrom;
      this.formData.dateTo = dateTo;
    },
    async loadReport() {
      this.loading = true;

      if (!this.$route.query.type) {
        this.$router.push({ name: 'cabinet' });
        return;
      }

      const { templateId } = this.$route.params;

      if (this.$validate.isUndefined(templateId)) {
        this.loading = false;
        return;
      }

      try {
        const { data } = await fetchTemplateByIdApi(templateId);
        this.formData = {
          ...this.formData,
          transactionTypes: data.transactionTypes,
          transactionStatuses: data.transactionStatuses,
          dateFrom: moment(data.dateFrom),
          dateTo: moment(data.dateTo),
          partners: data.projects,
          metrics: data.fields,
          id: data.id,
        };

        this.setUploadingPeriod();
        this.templateName = data.name;
      } catch (error) {
        console.error(`${error.name}: ${error.message}`);
      }

      this.loading = false;
    },
    async loadMetrics() {
      try {
        const { data } = await fetchReportFeildsApi(this.reportType);

        this.metrics = data;
      } catch (error) {
        console.error(`${error.name}: ${error.message}`);
      }
    },
    handleShownDateRange() {
      // TODO: передавать период дат с учетом таймзоны мерчанта
      EVENT_BUS.$emit('SELECTING_DATE_RANGE', {
        dateFrom: moment(this.formData.dateFrom),
        dateTo: moment(this.formData.dateTo),
      });
    },
    handleManagingTemplate() {
      if (this.$validate.isEmptyString(this.templateName)) {
        EVENT_BUS.$emit('EVENT_RENAME_REPORT_TEMPLATE');
        return;
      }

      this.saveReportTemplate();
    },
    async saveReportTemplate() {
      this.loading = true;

      const isEditing = this.$validate.isNotNull(this.formData.id);

      try {
        if (isEditing) {
          await editTemplateApi(this.templatePayload, this.formData.id);
        } else {
          await createTemplateApi(this.templatePayload);
        }

        this.$alert({
          type: 'success',
          text: isEditing
            ? 'notification.report.template.edited.success'
            : 'notification.report.template.created.success',
        });
      } catch (error) {
        this.$alert({
          type: 'warning',
          text: isEditing
            ? 'notification.report.template.edited.error'
            : 'notification.report.template.created.error',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="sass" module>
  .container
    width: 100%
    max-width: 460px
    margin: 0 auto
    padding: 20px 0px 40px

    +respond-to('medium')
      padding: 60px 0

  .header
    position: relative
    margin-bottom: 20px
    padding: 0 20px

    +respond-to('small')
      padding: 0

  .link
    +base-link
    white-space: nowrap
    text-decoration: underline
    cursor: pointer

    +hover
      text-decoration: none

  .building
    display: flex
    justify-content: center
    margin-top: 60px
    padding: 0 20px

    +respond-to('medium')
      padding: 0 105px

  .saving
    display: flex
    justify-content: center
    margin-top: 30px
</style>
